import styled from "styled-components";
import { useState } from "react";
import { Link } from "@inertiajs/react";
import {
  ShieldCheck,
  Brush,
  Tag,
  ChevronDown,
  CalendarRange,
  Lock,
} from "lucide-react";

import { theme } from "./theme";
import RateSimulationModal from "./RateSimulationModal";
import { useT } from "./i18n";
import { formatDate, wibTodayIso } from "./dateFormat";
import BulkBlockModal from "./BulkBlockModal";

// The operational "pulse" band: availability vs eZee + the two work queues + walk-in rates.
// Information-dense but scannable — grouped blocks, soft surfaces, hover tooltips.
const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 0.6rem;
  margin-bottom: 1rem;
`;

const Block = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.6rem 0.75rem;
  background: #ffffff;
  border: 1px solid ${theme.border};
  border-radius: 12px;
`;

const BlockTitle = styled.div`
  font-size: 0.74rem;
  font-weight: 700;
  color: ${theme.muted};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
`;

const AvChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.45rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  color: ${(props) => (props.$mismatch ? "#b91c1c" : theme.ink700)};
  background: ${(props) => (props.$mismatch ? "#fdecec" : theme.canvas)};
  border: 1px solid ${(props) => (props.$mismatch ? "#f2c4c0" : theme.border)};
`;

const Hint = styled.div`
  font-size: 0.74rem;
  color: ${theme.muted};
`;

const Queue = styled.a`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.6rem 0.75rem;
  min-width: 150px;
  background: #ffffff;
  border: 1px solid ${theme.border};
  border-radius: 12px;
  text-decoration: none;
  cursor: pointer;
  &:hover {
    border-color: ${theme.gold};
    background: ${theme.goldSoft};
  }
`;

const QueueTop = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: ${theme.ink};
`;

const QueueTitle = styled.span`
  font-size: 0.78rem;
  font-weight: 700;
`;

const Count = styled.span`
  margin-left: auto;
  min-width: 1.4rem;
  padding: 0 0.35rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 1.4rem;
  border-radius: 999px;
  background: ${(props) => (props.$zero ? theme.border : theme.ink)};
  color: ${(props) => (props.$zero ? theme.muted : "#ffffff")};
  font-size: 0.78rem;
  font-weight: 800;
`;

const Codes = styled.div`
  font-size: 0.74rem;
  color: ${theme.muted};
  max-width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RatesBlock = styled.div`
  position: relative;
`;

const RatesButton = styled.button`
  height: 100%;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.6rem 0.75rem;
  background: #ffffff;
  border: 1px solid ${theme.border};
  border-radius: 12px;
  font-size: 0.78rem;
  font-weight: 700;
  color: ${theme.ink};
  cursor: pointer;
  &:hover {
    border-color: ${theme.gold};
    background: ${theme.goldSoft};
  }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 40;
`;

const RatesPopover = styled.div`
  position: absolute;
  top: calc(100% + 0.35rem);
  right: 0;
  z-index: 50;
  width: 340px;
  max-width: calc(100vw - 2rem);
  padding: 0.75rem;
  background: #ffffff;
  border: 1px solid ${theme.border};
  border-radius: 10px;
  box-shadow: 0 14px 34px -12px rgba(26, 26, 26, 0.35);
`;

const PopSubHead = styled.div`
  border-top: 1px solid ${theme.border};
  color: #4a4f63;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  margin-top: 0.45rem;
  padding-top: 0.4rem;
  text-transform: uppercase;
`;

const RateRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.5rem 0.25rem;
  font-size: 0.9rem;
  border-bottom: 1px solid ${theme.border};
  &:last-child {
    border-bottom: 0;
  }
`;

const RateName = styled.span`
  color: ${theme.ink700};
  line-height: 1.35;
`;

const RateValue = styled.span`
  font-weight: 700;
  color: ${theme.ink};
  white-space: nowrap;
`;

const PopHint = styled.div`
  font-size: 0.74rem;
  color: ${theme.muted};
  margin: 0.1rem 0.15rem 0.35rem;
`;

const DateLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0 0.15rem 0.5rem;
  font-size: 0.92rem;
  font-weight: 800;
  color: ${theme.ink};
`;

const TodayBadge = styled.span`
  padding: 0.05rem 0.4rem;
  border-radius: 999px;
  background: ${theme.gold};
  color: ${theme.ink};
  font-size: 0.66rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const SimButton = styled.button`
  margin-top: 0.6rem;
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid ${theme.goldDark};
  border-radius: 9px;
  background: ${theme.ink};
  color: ${theme.gold};
  font-size: 0.82rem;
  font-weight: 800;
  cursor: pointer;
  &:hover {
    background: ${theme.inkSoft};
  }
`;

const PropertyStatusBar = (props) => {
  const { t, lang } = useT();
  const data = props.data || {};
  const availability = data.availability || [];
  const secured = data.awaiting_secured || { count: 0, codes: [] };
  const clean = data.awaiting_clean || { count: 0, codes: [] };
  const rates = data.walk_in_rates || [];
  const transitRates = data.transit_rates || [];
  const [ratesOpen, setRatesOpen] = useState(false);
  const [simOpen, setSimOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const anyMismatch = availability.some((entry) => entry.mismatch);
  const isToday = data.date === wibTodayIso();

  return (
    <>
      <Bar>
        <Block>
          <BlockTitle>{t("ps_availability")}</BlockTitle>
          <Chips>
            {availability.length === 0 ? (
              <Hint>{t("ps_none")}</Hint>
            ) : (
              availability.map((entry) => (
                <AvChip
                  key={entry.code}
                  $mismatch={entry.mismatch}
                  title={`${entry.name} — kami ${entry.own}, eZee ${entry.ezee ?? "—"}`}
                >
                  {entry.code}: {entry.own} / {entry.ezee ?? "—"}
                </AvChip>
              ))
            )}
          </Chips>
          {anyMismatch ? <Hint>{t("ps_availability_hint")}</Hint> : null}
        </Block>

        <Queue
          as={Link}
          href="/frontdesk_v2/housekeeping"
          title={`${t("ps_awaiting_secured_hint")} ${secured.codes.join(", ")}`}
        >
          <QueueTop>
            <ShieldCheck size={16} />
            <QueueTitle>{t("ps_awaiting_secured")}</QueueTitle>
            <Count $zero={secured.count === 0}>{secured.count}</Count>
          </QueueTop>
          <Codes>
            {secured.codes.length > 0 ? secured.codes.join(", ") : "—"}
          </Codes>
        </Queue>

        <Queue
          as={Link}
          href="/frontdesk_v2/housekeeping"
          title={t("ps_awaiting_clean_hint")}
        >
          <QueueTop>
            <Brush size={16} />
            <QueueTitle>{t("ps_awaiting_clean")}</QueueTitle>
            <Count $zero={clean.count === 0}>{clean.count}</Count>
          </QueueTop>
          <Codes>{clean.codes.length > 0 ? clean.codes.join(", ") : "—"}</Codes>
        </Queue>

        {rates.length > 0 ? (
          <RatesBlock>
            <RatesButton type="button" onClick={() => setRatesOpen(!ratesOpen)}>
              <Tag size={16} />
              {t("ps_rates")}
              <ChevronDown size={14} />
            </RatesButton>
            {ratesOpen ? (
              <>
                <Backdrop onClick={() => setRatesOpen(false)} />
                <RatesPopover>
                  <PopHint>{t("ps_rates_hint")}</PopHint>
                  <DateLine>
                    {formatDate(data.date, lang)}
                    {isToday ? <TodayBadge>{t("today")}</TodayBadge> : null}
                  </DateLine>
                  {rates.map((rate) => (
                    <RateRow key={rate.code}>
                      <RateName>{rate.name}</RateName>
                      <RateValue>{rate.formatted}</RateValue>
                    </RateRow>
                  ))}
                  {/* Hours as well as nights: the property sells both, and the bar used to show only
                      one of them. */}
                  {transitRates.length > 0 ? (
                    <>
                      <PopSubHead>{t("ps_rates_transit")}</PopSubHead>
                      {transitRates.map((rate) => (
                        <RateRow key={rate.code}>
                          <RateName>
                            {rate.name} · {rate.duration_hours}{" "}
                            {t("transit_hours")}
                          </RateName>
                          <RateValue>{rate.formatted}</RateValue>
                        </RateRow>
                      ))}
                    </>
                  ) : null}
                  <SimButton
                    type="button"
                    onClick={() => {
                      setRatesOpen(false);
                      setSimOpen(true);
                    }}
                  >
                    <CalendarRange size={14} />
                    {t("ps_rates_simulate")}
                  </SimButton>
                </RatesPopover>
              </>
            ) : null}
          </RatesBlock>
        ) : null}

        {/* Peta Kamar only: the operator picks rooms and a date range here, and Beranda has no
            room context to act on. Kept OUTSIDE the rates block above, which is hidden on a day
            with no walk-in rates — bulk blocking must not disappear with it.

            Wrapped in RatesBlock like its sibling for a reason: RatesButton sets height: 100%,
            which as a direct flex child resolves against an indefinite container height and
            collapses the button to its content (37px against the siblings' 62px). The wrapper is
            the flex item that stretches, and 100% then means the full row height. */}
        {props.showBulkBlock ? (
          <RatesBlock>
            <RatesButton type="button" onClick={() => setBulkOpen(true)}>
              <Lock size={16} />
              {t("bulk_block")}
            </RatesButton>
          </RatesBlock>
        ) : null}
      </Bar>
      {simOpen ? (
        <RateSimulationModal onClose={() => setSimOpen(false)} />
      ) : null}
      {bulkOpen ? (
        <BulkBlockModal date={data.date} onClose={() => setBulkOpen(false)} />
      ) : null}
    </>
  );
};

export default PropertyStatusBar;
